import { collection } from '../infra/collection'
import {
  CreateScopeWorkJobDto,
  OCRManualInputsQuery,
  OCRScopeWorkDto,
  OCRScopeWorkSearchQuery,
  LinkManuallyScopeWorkJobDto,
} from './dtos/ocr-scope-work'
import OCRScopeWork from './OCRScopeWork'
import { OCRScopeWorkAPI } from './API'
import {
  ScopeJobCreatedMessage,
  ScopeJobArchivedMessage,
  ScopeJobUpdatedMessage,
  ScopeJobNeedsAttentionMessage,
  ScopeJobSubmittedMessage,
  ScopeJobErroredMessage,
} from './dtos/realtime-messages'
import { Observable, share } from 'rxjs'
import { Env } from 'infra/env'

export default class OCRScopeWorkStore {
  ocrScopeWorks = collection<OCRScopeWorkDto, OCRScopeWork>({
    create: () => new OCRScopeWork(),
  })

  scopeJobCreatedEvents$?: Observable<ScopeJobCreatedMessage>

  scopeJobNeedsAttentionEvents$?: Observable<ScopeJobNeedsAttentionMessage>

  scopeJobSubmittedEvents$?: Observable<ScopeJobSubmittedMessage>

  scopeJobErroredEvents$?: Observable<ScopeJobErroredMessage>

  scopeJobUpdatedEvents$?: Observable<ScopeJobUpdatedMessage>

  scopeJobArchivedEvents$?: Observable<ScopeJobArchivedMessage>

  constructor(private api: OCRScopeWorkAPI) {
    // Since Wunder API is an optional service, we don't subscribe to realtime events if WUNDER_API_EVENTS is false.
    const isDev = process.env.NODE_ENV === 'development'
    if (isDev && !Env.WUNDER_API_EVENTS) {
      return
    }

    const createdUpdates$ = this.api
      .when<ScopeJobCreatedMessage>('ScopeJobCreated')
      .pipe(share())
    const needsAttentionUpdates$ = this.api
      .when<ScopeJobNeedsAttentionMessage>('ScopeJobNeedsAttention')
      .pipe(share())
    const submittedUpdates$ = this.api
      .when<ScopeJobSubmittedMessage>('ScopeJobSubmitted')
      .pipe(share())
    const erroredUpdates$ = this.api
      .when<ScopeJobErroredMessage>('ScopeJobErrored')
      .pipe(share())
    const updatedUpdates$ = this.api
      .when<ScopeJobUpdatedMessage>('ScopeJobUpdated')
      .pipe(share())
    const archiveUpdates$ = this.api
      .when<ScopeJobArchivedMessage>('ScopeJobArchived')
      .pipe(share())

    this.scopeJobCreatedEvents$ = createdUpdates$
    this.scopeJobNeedsAttentionEvents$ = needsAttentionUpdates$
    this.scopeJobSubmittedEvents$ = submittedUpdates$
    this.scopeJobErroredEvents$ = erroredUpdates$
    this.scopeJobUpdatedEvents$ = updatedUpdates$
    this.scopeJobArchivedEvents$ = archiveUpdates$
  }

  async fetch(workspaceId: string, scopeWorkId: string) {
    const searchResults = await this.api.search({
      limit: 1,
      workspace_id: workspaceId,
      search_text: '',
      aggregations: `archived:false,id:${scopeWorkId}`,
    })

    if (searchResults.data.length === 0) {
      return OCRScopeWork.none
    }
    return this.ocrScopeWorks.set(searchResults.data[0])
  }

  async search(query: OCRScopeWorkSearchQuery) {
    const searchResults = await this.api.search(query)

    return {
      data: this.ocrScopeWorks.set(searchResults.data),
      cursor: searchResults.cursor,
    }
  }

  async aggregations(workspaceId: string) {
    return await this.api.aggregations(workspaceId)
  }

  async manualInputs(data: OCRManualInputsQuery) {
    const result = await this.api.manualInputs(data)
    return result?.manual_inputs || []
  }

  async scopeWork(data: CreateScopeWorkJobDto) {
    return await this.api.scopeWork(data)
  }

  async archive(scopeWorkId: string) {
    return await this.api.archive(scopeWorkId)
  }

  async rerun(scopeWorkId: string) {
    return await this.api.rerun(scopeWorkId)
  }

  async linkManually(scopeWorkId: string, data: LinkManuallyScopeWorkJobDto) {
    return await this.api.linkManually(scopeWorkId, data)
  }

  async review(scopeWorkId: string) {
    return await this.api.review(scopeWorkId)
  }
}
